import { ThemeEvents } from '@theme/events';
import { trackIgEvent } from '@theme/intelligems-events';

class ProductMediaGallery extends HTMLElement {
  #closestSection = null;
  #splide = null;
  #arrowClickRoot = null;
  #onArrowClick = null;
  #onSlideMoved = null;

  connectedCallback() {
    this.#closestSection = this.closest('.shopify-section, dialog');
    if (!this.#closestSection) return;
    this.#closestSection.addEventListener(ThemeEvents.variantUpdated, this.updateMediaGallery);

    // Le slider enfant monte Splide dans son connectedCallback : attendre la fin du cycle
    queueMicrotask(() => {
      if (!this.isConnected) return;
      this.#setupIgTracking();
    });
  }

  disconnectedCallback() {
    this.#teardownIgTracking();

    if (!this.#closestSection) return;
    this.#closestSection.removeEventListener(ThemeEvents.variantUpdated, this.updateMediaGallery);
    this.#closestSection = null;
  }

  updateMediaGallery = (event) => {
    if (!this.isConnected) return;
    if (!this.#isRelevantVariantUpdate(event)) return;

    const newMediaGallery = event.detail.data.html.querySelector('product-media-gallery slider-component');
    const currentMediaGallery = this.querySelector('slider-component');

    if (!newMediaGallery || !currentMediaGallery) return;

    if (this.#isPartialQuickAddGallery()) {
      this.#updatePartialGallery(event, newMediaGallery, currentMediaGallery);
      return;
    }

    if (currentMediaGallery.innerHTML !== newMediaGallery.innerHTML) {
      this.#teardownIgTracking();
      currentMediaGallery.replaceWith(newMediaGallery);
      this.#setupIgTracking();
    }
  };

  #setupIgTracking() {
    this.#teardownIgTracking();

    const sliderComponent = this.querySelector('slider-component');
    const mainSliderEl = this.querySelector('[data-ref="main-slider"]');
    const splide = sliderComponent?.slider;

    if (!mainSliderEl || !splide) return;

    this.#splide = splide;
    this.#arrowClickRoot = mainSliderEl;

    this.#onArrowClick = (event) => {
      const arrow = event.target instanceof Element ? event.target.closest('.splide__arrow') : null;
      if (!arrow || !mainSliderEl.contains(arrow)) return;

      const direction = arrow.classList.contains('splide__arrow--prev') ? 'prev' : 'next';
      trackIgEvent(`productMediaGalleryArrowClick_${this.dataset.igProductTitle}`, { direction });
    };

    this.#onSlideMoved = (newIndex, prevIndex) => {
      const slide = mainSliderEl.querySelectorAll('.splide__slide')[newIndex];

      trackIgEvent(`productMediaGallerySlideChange_${this.dataset.igProductTitle}`, {
        index: newIndex + 1,
        previousIndex: prevIndex + 1,
        mediaId: slide?.dataset?.mediaId || '',
      });
    };

    mainSliderEl.addEventListener('click', this.#onArrowClick);
    splide.on('moved', this.#onSlideMoved);
  }

  #teardownIgTracking() {
    if (this.#arrowClickRoot && this.#onArrowClick) {
      this.#arrowClickRoot.removeEventListener('click', this.#onArrowClick);
    }

    if (this.#splide && this.#onSlideMoved) {
      this.#splide.off('moved', this.#onSlideMoved);
    }

    this.#arrowClickRoot = null;
    this.#onArrowClick = null;
    this.#onSlideMoved = null;
    this.#splide = null;
  }

  #isRelevantVariantUpdate(event) {
    if (!(event.target instanceof Element)) return false;

    const popupContent = this.closest('[data-ref="popup-content"]');
    const eventPopupContent = event.target.closest('[data-ref="popup-content"]');

    return popupContent === eventPopupContent;
  }

  #isPartialQuickAddGallery() {
    return this.dataset.galleryMode === 'partial';
  }

  #updatePartialGallery(event, newMediaGallery, currentMediaGallery) {
    const currentSlide = currentMediaGallery.querySelector('.splide__slide.product-media-gallery__media');
    if (!currentSlide) return;

    const newSlides = newMediaGallery.querySelectorAll('.splide__slide.product-media-gallery__media');
    if (newSlides.length === 0) return;

    const featuredMediaId = event.detail.resource?.featured_media?.id;
    const newSlide =
      featuredMediaId != null
        ? newMediaGallery.querySelector(`.splide__slide.product-media-gallery__media[data-media-id="${featuredMediaId}"]`)
        : newSlides[0];

    const slideToApply = newSlide ?? newSlides[0];

    if (currentSlide.outerHTML === slideToApply.outerHTML) return;

    currentSlide.dataset.mediaId = slideToApply.dataset.mediaId;
    currentSlide.className = slideToApply.className;
    currentSlide.innerHTML = slideToApply.innerHTML;
  };
}

if (!customElements.get('product-media-gallery')) {
  customElements.define('product-media-gallery', ProductMediaGallery);
}
